import React, { FocusEventHandler, ReactNode, useId } from 'react';
import Select, {
	ControlProps,
	DropdownIndicatorProps,
	GroupBase,
	MenuProps,
	SingleValueProps,
	StylesConfig,
	ValueContainerProps,
	components
} from 'react-select';
import { Icon, IconName } from '../../foundations';
import { Text } from '../../foundations';
import styles from './InputSelect.module.css';

type InputSelectOption = {
	value: string;
	label: string;
};

type InputSelectProps<T = InputSelectOption> = {
	/**
	 * Indicates the name of the Input Select
	 * @default null
	 */
	name: string;

	/**
	 * Indicates the availability of the Input Select
	 * @default null
	 */
	disabled?: boolean;

	/**
	 * Indicates the icon displayed in the Input Select
	 * @default null
	 */
	icon?: IconName;

	/**
	 * Indicates the text displayed as Input label
	 * @default null
	 */
	label?: ReactNode;

	/**
	 * Indicates the text displayed as ariaLabel
	 * @default null
	 */
	ariaLabel?: string;

	/**
	 * Indicates the error message text
	 * @default null
	 */
	errorMessage?: string;

	/**
	 * Indicates the children displayed as Information
	 * @default null
	 */
	information?: ReactNode;

	/**
	 * Indicates the options displayed
	 * @default null
	 */
	options: T[];

	/**
	 * defaultValue
	 */
	defaultValue?: T | null;

	/**
	 * onBlur event
	 */
	onBlur?: FocusEventHandler<HTMLInputElement> | undefined;

	/**
	 * onChange event
	 */
	onChange?: (newValue: T) => void;

	/**
	 * selected value
	 */
	value?: T | null;

	/**
	 * Styles of the container of the input
	 */
	className?: string;
};

const selectStyles = (
	errorMessage: string | undefined,
	disabled?: boolean
): StylesConfig<unknown, boolean, GroupBase<unknown>> | undefined => {
	return {
		container: (baseStyles) => ({
			...baseStyles
		}),
		control: (_, state) => ({
			display: 'flex',
			width: '100%',
			height: '2.5rem',
			padding: '0rem 0.5rem',
			alignItems: 'center',
			justifyContent: 'space-between',
			gap: '0.5rem',
			outline: 'none',
			flexShrink: 0,
			border: state.isFocused
				? '0.063rem solid var(--primary-orange, #D68726)'
				: errorMessage
					? '0.063rem solid var(--primary-red-700, #A8191A)'
					: '0.063rem solid var(--grey-400-cc, #CCC)',
			background: disabled ? 'var(--grey-200-ee, #EEE)' : 'var(--neutrals-white, #FFF)'
		}),
		indicatorSeparator: (baseStyles) => ({
			...baseStyles,
			display: 'none'
		}),
		dropdownIndicator: (baseStyles, state) => ({
			...baseStyles,
			transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none'
		}),
		menuPortal: (baseStyles) => ({
			...baseStyles,
			zIndex: 10
		}),
		menu: (baseStyles) => ({
			...baseStyles,
			zIndex: 10,
			margin: 0
		}),
		singleValue: (baseStyles) => ({
			...baseStyles,
			overflow: 'visible',
			color: 'var(--grey-800-33)',
			fontSize: '1rem',
			'&::first-letter': {
				textTransform: 'uppercase'
			}
		}),
		option: (baseStyles, state) => ({
			display: 'flex',
			width: '100%',
			padding: '0.5rem 1rem',
			alignItems: 'flex-start',
			gap: '0.5rem',
			background: state.isFocused ? 'var(--grey-100-fa, #FAFAFA)' : 'var(--neutrals-white, #FFF)'
		}),
		menuList: (baseStyles) => ({
			...baseStyles,
			outline: 'none',
			background: 'var(--neutrals-white, #FFF)',
			boxShadow: '0 0.25rem 0.25rem 0 rgba(0, 0, 0, 0.25)'
		}),
		valueContainer: (baseStyles, state) => {
			const stateAugmented = state as ValueContainerProps & { selectProps: { icon: string; label: ReactNode } };
			return {
				...baseStyles,
				padding: '0',
				overflow: 'visible',
				display: 'flex',
				flexDirection: 'row',
				alignContent: 'flex-start',
				justifyContent: 'flex-start',
				transform: stateAugmented.selectProps.label ? 'translateY(0.5rem)' : ''
			};
		}
	};
};

const DropdownIndicator = (props: DropdownIndicatorProps) => {
	return (
		<components.DropdownIndicator className={styles.inputSelectArrowIcon} {...props}>
			<Icon name="caret-abajo"></Icon>
		</components.DropdownIndicator>
	);
};

const Control = (props: ControlProps & { selectProps: { icon: IconName | undefined } }) => {
	return (
		<>
			<components.Control {...props}>
				{props.selectProps.icon ? (
					<Icon size={24} className={styles.inputSelectIcon} name={props.selectProps.icon}></Icon>
				) : null}
				{props.children}
			</components.Control>
		</>
	);
};

const ValueContainer = (props: ValueContainerProps & { selectProps: { label: string } }) => {
	return (
		<components.ValueContainer {...props}>
			<label
				className={props.selectProps.value ? styles.inputSelectLabelOpen : styles.inputSelectLabelClosed}
				id={props.selectProps['aria-labelledby']}
			>
				{props.selectProps.label}
			</label>
			{props.children}
		</components.ValueContainer>
	);
};

const SingleValue = (props: SingleValueProps) => {
	return (
		<components.SingleValue className={styles.valueSelected} {...props}>
			{props.children}
		</components.SingleValue>
	);
};

const Menu = (props: MenuProps) => {
	return (
		<>
			<components.Menu {...props} className={styles.animatedMenu} />
		</>
	);
};

const ErrorMessage = (errorMessage: string) => {
	return (
		<div className={styles.errorMessage}>
			<Text font="verdana" size="lg-regular">
				{errorMessage}
			</Text>
		</div>
	);
};

const InformationContent = (information: ReactNode) => {
	return <div className={styles.information}>{information}</div>;
};

export const InputSelect = React.forwardRef(
	(
		{
			name,
			disabled,
			defaultValue,
			information,
			errorMessage,
			className,
			icon,
			label,
			ariaLabel,
			options,
			onChange,
			onBlur,
			value
		}: InputSelectProps,
		// eslint-disable-next-line
		ref: React.Ref<any> | undefined
	) => {
		const identifier = useId();

		return (
			<div className={className}>
				<Select
					id={identifier + '-input'}
					aria-errormessage={errorMessage ? identifier + '-error' : undefined}
					aria-labelledby={identifier + '-label'}
					aria-invalid={Boolean(errorMessage)}
					ref={ref}
					defaultValue={defaultValue}
					value={value}
					icon={icon}
					placeholder=""
					label={label}
					aria-label={ariaLabel}
					name={name}
					isDisabled={disabled}
					options={options}
					isSearchable={false}
					onChange={(e) => {
						const newValue = e as InputSelectOption;
						onChange?.(newValue);
					}}
					onBlur={onBlur}
					noOptionsMessage={() => null}
					maxMenuHeight={180}
					menuPlacement="auto"
					menuPosition="fixed"
					styles={selectStyles(errorMessage, disabled)}
					//@ts-expect-error react-select library doesn´t support custom properties. https://react-select.com/typescript
					components={{ DropdownIndicator, Control, ValueContainer, SingleValue, Menu }}
				/>
				{errorMessage ? ErrorMessage(errorMessage) : null}
				{information ? InformationContent(information) : null}
			</div>
		);
	}
);

InputSelect.displayName = 'InputAutocomplete';
